import React, { useCallback, useRef, useState } from 'react';

import { Modal, ModalBody, ModalHeader } from '@change/ds-core-ui/Modal';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useShareEducationModalContext } from '../../shareEducationModal';
import type { OpenModalResult } from '../../shareEducationModal';

import { EducationModalBody } from './components/EducationModalBody';
import type { UseStoriesEducationModalProps } from './types';

export type OpenStoriesEducationModalFn = (options: UseStoriesEducationModalProps) => OpenModalResult;

/**
 * Returns [openModal, modal | null].
 *
 * When `ShareEducationModalProvider` is present in the tree the hook delegates
 * to the shared modal (modal === null, rendering is handled by ShareSlideUp).
 * Otherwise it falls back to a locally-managed modal instance.
 */
export function useEducationModal(): [openModal: OpenStoriesEducationModalFn, modal: React.ReactElement | null] {
	const openSharedModal = useShareEducationModalContext();
	const { translate } = useI18n();

	const heading = translate('corgi.psf.share.modals.heading.title', { shareChannel: 'Instagram' });

	// Local fallback
	const [localIsOpen, setLocalIsOpen] = useState(false);
	const [localOptions, setLocalOptions] = useState<UseStoriesEducationModalProps | null>(null);
	const localResolverRef = useRef<((result: { successfulShare: boolean } | undefined) => void) | null>(null);

	const localHandleClose = useCallback((result?: { successfulShare: boolean }) => {
		setLocalIsOpen(false);

		localResolverRef.current?.(result);
		localResolverRef.current = null;
	}, []);

	const localOpenModal = useCallback((options: UseStoriesEducationModalProps): OpenModalResult => {
		setLocalOptions(options);
		setLocalIsOpen(true);

		/**
		 * Need to resolve any prior pending promise so it doesn't hang
		 * in case users open the modal multiple times.
		 */
		localResolverRef.current?.(undefined);

		// oxlint-disable-next-line promise/avoid-new
		const promise = new Promise<{ successfulShare: boolean } | undefined>((resolve) => {
			localResolverRef.current = resolve;
		});

		return { waitForResult: async () => promise };
	}, []);

	// Shared-context path — builds Instagram Stories content and delegates to shared modal.
	const sharedOpenModal = useCallback(
		(options: UseStoriesEducationModalProps): OpenModalResult => {
			return openSharedModal
				? openSharedModal((close) => ({
						heading,
						body: (
							<EducationModalBody
								onContinue={async () => {
									await options.onContinueAction();
									close({ successfulShare: true });
								}}
								shareVisualPreview={options.shareVisualPreview}
							/>
						),
					}))
				: { waitForResult: async () => undefined };
		},
		[openSharedModal, heading],
	);

	if (openSharedModal) {
		return [sharedOpenModal, null];
	}

	const localModal = (
		<Modal
			isOpen={localIsOpen}
			onClose={() => localHandleClose(undefined)}
			onOpenChange={(open) => {
				if (!open) localHandleClose(undefined);
			}}
			transformToSheet
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			<ModalHeader title={heading} />
			<ModalBody>
				{localOptions && (
					<EducationModalBody
						onContinue={async () => {
							await localOptions.onContinueAction();
							localHandleClose({ successfulShare: true });
						}}
						shareVisualPreview={localOptions.shareVisualPreview}
					/>
				)}
			</ModalBody>
		</Modal>
	);

	return [localOpenModal, localModal];
}
